import { apiConfig } from '@/api/config';
import { EntitlementHeaders } from '@/api/headers';
import { ApiLogger } from '@/api/logger/apiLogger';
import { osDetector } from '@/api/osDetector/client';
import type { FavoriteEntity, FavoriteEntityType } from '@/api/types/entityTypes';
import type { Favorite, FavoriteId, FavoritesResponse } from '@/api/types/favoriteTypes';

import { type HttpOptions } from '../server/httpServer';
import { HttpClient } from './httpClient';

const { apiKey, entitlementBaseUrl } = apiConfig;

// hard-code default favoriteIds
const nfl: FavoriteId = 'league:football/nfl/league/1';
const cfb: FavoriteId = 'league:football/cfb/league/1';
const mlb: FavoriteId = 'league:baseball/mlb/league/1';

export const defaultFavoriteIds: FavoriteId[] = [nfl, cfb, mlb];

/**
 * Favorites API Client
 * @see https://teamfox.atlassian.net/wiki/spaces/FOXDTC/pages/2359754860/Favorites+API+Spec
 */
export class FavoritesClient {
  public constructor(private httpClient: HttpClient) {}

  public async getUserFavoriteIds(filterBy?: FavoriteEntityType[]): Promise<FavoriteId[]> {
    const data = await this.httpClient.get<FavoritesResponse>('/favorites/v1/favorites');

    const filter = filterBy?.map((p) => p.toLowerCase()) ?? [];

    const explicitFavoriteIds = data.favorites.filter((fav) => fav.type === 'explicit');
    const uniqueFavoriteIds = Array.from(new Set(explicitFavoriteIds.map((fav) => fav.entity)));

    if (uniqueFavoriteIds.length > 0) {
      if (filterBy) {
        const favoriteIds = explicitFavoriteIds.filter((fav) => filter.includes(fav.entity.split(':')[0]));

        return Array.from(new Set(favoriteIds.map((fav) => fav.entity)));
      }

      return uniqueFavoriteIds;
    }

    if (!filterBy || filter.includes('league')) {
      return defaultFavoriteIds;
    }

    return [];
  }

  public async getUserFavorites(options: HttpOptions): Promise<Favorite[]> {
    const data = await this.httpClient.get<FavoritesResponse>('/favorites/v1/favorites', options);

    const explicitFavorites = data.favorites.filter((fav) => fav.type === 'explicit');

    if (explicitFavorites.length > 0) {
      return explicitFavorites;
    }

    return [];
  }

  public async updateUserFavorites(userFavorites: FavoriteEntity[]): Promise<[boolean, Nullable<FavoriteId[]>]> {
    const results: [boolean, Nullable<FavoriteId[]>] = [false, null];

    const deletedFavorites = userFavorites.filter((fav) => !fav.isFavorite);
    const addedFavorites = userFavorites.filter((fav) => fav.isFavorite);

    const resolvedPromises = await Promise.all([
      this.deleteFavorites(deletedFavorites),
      this.addFavorites(addedFavorites),
    ]);

    // boolean indicates whether there were favorites to be deleted
    results[0] = resolvedPromises[0];

    // favoriteIds that were updated/returned by the server
    if (resolvedPromises[1]) {
      results[1] = resolvedPromises[1];
    }

    return results;
  }

  private getUniqueFavoriteIds(favorites: FavoriteEntity[]): FavoriteId[] {
    const favoriteIds = favorites.map((fav) => fav.favoriteId);
    const uniqueFavoriteIds = Array.from(new Set(favoriteIds));

    return uniqueFavoriteIds;
  }

  private async addFavorites(addedFavorites: FavoriteEntity[]): Promise<Nullable<FavoriteId[]>> {
    const uniqueFavoriteIds = this.getUniqueFavoriteIds(addedFavorites);

    if (uniqueFavoriteIds.length > 0) {
      const data = await this.httpClient.put<FavoritesResponse>('/favorites/v1/favorites', {
        favorites: uniqueFavoriteIds,
      });

      if (data) {
        return data.favorites.map((fav) => fav.entity);
      }
    }

    return null;
  }

  private async deleteFavorites(deletedFavorites: FavoriteEntity[]): Promise<boolean> {
    const uniqueFavoriteIds = this.getUniqueFavoriteIds(deletedFavorites);

    const favoriteIds = uniqueFavoriteIds.map((id) => ({ id }));

    if (favoriteIds.length > 0) {
      await this.httpClient.delete('/favorites/v1/favorites', favoriteIds);

      return true;
    }

    return false;
  }
}

const logger = new ApiLogger('FavoritesClient');
const headers = new EntitlementHeaders(apiKey);
const httpClient = new HttpClient({ baseUrl: entitlementBaseUrl, logger, headers, osDetector });

export const favoritesClient = new FavoritesClient(httpClient);
